import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, Phone, MapPin, Briefcase, Calendar, Clock,
  MessageSquare, Play, DollarSign, FileText, User,
  ChevronDown, ChevronRight, Loader2, Save, Check, X,
  RotateCcw, Pause, AlertTriangle, ExternalLink, Send, Trash2
} from "lucide-react";
import { getLeads, saveLeads, getActionSuggestion, PLAN_DATA, CLOSER_ACCOUNTS } from "./demoConfig";
import { fetchLeads, waGetMessages, waMarkRead, waStartSequence, waPauseSequence, waResumeSequence, waSendPdf, waExecuteStep, waGetFollowUpRules, updateLeadField, deleteLeadAPI } from "./api";
import WhatsAppChat from "./WhatsAppChat";
import CallIntelligence from "./CallIntelligence";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_CONFIG = {
  new: { label: "New", color: "#8B5CF6", bg: "#F5F3FF" },
  upcoming: { label: "Upcoming", color: "#3B82F6", bg: "#EFF6FF" },
  demo_in_progress: { label: "In Progress", color: "#8B5CF6", bg: "#F5F3FF" },
  demo_done: { label: "Demo Done", color: "#F59E0B", bg: "#FFFBEB" },
  payment_sent: { label: "Payment Sent", color: "#F97316", bg: "#FFF7ED" },
  converted: { label: "Converted", color: "#10B981", bg: "#ECFDF5" },
  cold: { label: "Cold", color: "#6B7280", bg: "#F3F4F6" },
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { auth } = useOutletContext();
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [msgCount, setMsgCount] = useState(0);
  const [seqLoading, setSeqLoading] = useState(false);
  const [stepSending, setStepSending] = useState(null);
  const [followUpRules, setFollowUpRules] = useState([]);
  const [pdfSending, setPdfSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteLead = async () => {
    setDeleting(true);
    try {
      await deleteLeadAPI(leadId);
      navigate("/demo/crm");
    } catch (e) {
      alert("Delete failed: " + (e?.message || "Unknown error"));
      setDeleting(false);
    }
  };

  useEffect(() => {
    const leads = getLeads();
    const found = leads.find(l => l.id === leadId);
    if (found) {
      setLead(found);
      setEditData(found);
    } else {
      // Fall back to API fetch when accessed directly (no parent context cache yet)
      fetchLeads()
        .then(all => {
          const fresh = (all || []).find(l => l.id === leadId);
          if (fresh) {
            setLead(fresh);
            setEditData(fresh);
          }
        })
        .catch(() => {});
    }
    // Load message count
    waGetMessages(leadId).then(d => setMsgCount(d.count || 0)).catch(() => {});
    waMarkRead(leadId).catch(() => {});
    // Load follow-up rules
    waGetFollowUpRules().then(d => setFollowUpRules(d.rules || [])).catch(() => {});
  }, [leadId]);

  const refreshLead = () => {
    const leads = getLeads();
    const found = leads.find(l => l.id === leadId);
    if (found) {
      setLead(found);
      setEditData(found);
    }
  };

  const handleSave = () => {
    const leads = getLeads();
    const updated = leads.map(l => l.id === leadId ? { ...l, ...editData } : l);
    saveLeads(updated);
    setLead({ ...lead, ...editData });
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const updateStatus = async (newStatus, extra = {}) => {
    const updates = { status: newStatus, ...extra };
    // Update local state immediately
    const leads = getLeads();
    const updated = leads.map(l => l.id === leadId ? { ...l, ...updates } : l);
    saveLeads(updated);
    setLead({ ...lead, ...updates });
    setEditData({ ...editData, ...updates });
    // Sync to backend API
    await updateLeadField(leadId, updates);
    // Refresh lead from backend after a short delay
    setTimeout(refreshLead, 500);
  };

  const handleSequenceAction = async (action) => {
    setSeqLoading(true);
    try {
      if (action === "start") await waStartSequence(leadId);
      else if (action === "pause") await waPauseSequence(leadId);
      else if (action === "resume") await waResumeSequence(leadId);
      // Refresh from DB
      setTimeout(refreshLead, 500);
    } catch (e) {
      console.error("Sequence action failed:", e);
    } finally {
      setSeqLoading(false);
    }
  };

  const handleExecuteStep = async (stepNum) => {
    setStepSending(stepNum);
    try {
      const rule = followUpRules.find(r => r.step === stepNum);
      const templateName = rule?.templateSlot || "";
      // Default messages for each step when no template
      const defaultMessages = {
        1: `Hey ${lead.clientName}! Great chatting today. Here's your personalized plan - ready to get started? Just say the word!`,
        2: `Hey ${lead.clientName}! Just checking in - had a chance to think about what we discussed? Any questions I can help with?`,
        3: `Hey ${lead.clientName}! One of our clients in a similar situation just got 5 interview calls in 2 weeks. Your spot is still open - want to lock it in?`,
        4: `Hey ${lead.clientName}, heads up - we're getting close to capacity this month. Shall I hold your spot?`,
        5: `Last follow-up from me, ${lead.clientName}! No pressure. If you're ready, just reply here and I'll get everything set up.`,
      };
      const message = templateName ? "" : (defaultMessages[stepNum] || `Follow-up step ${stepNum} for ${lead.clientName}`);
      await waExecuteStep(leadId, stepNum, { message, templateName });
      setTimeout(refreshLead, 500);
    } catch (e) {
      console.error("Step execution failed:", e);
      alert("Send Now failed: " + (e?.message || "Unknown error. Check Meta 24h window or template config."));
    } finally {
      setStepSending(null);
    }
  };

  const handleMarkPaid = async () => {
    try {
      const amt = editData.planAmount ?? lead.planAmount ?? (lead.selectedPlan ? PLAN_DATA[lead.selectedPlan]?.priceNum : null) ?? lead.convertedAmount ?? 0;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/demo/leads/${leadId}/mark-paid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Number(amt), plan: editData.selectedPlan || lead.selectedPlan || lead.recommendedPlan }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Mark paid failed"); }
      const data = await r.json();
      setLead({ ...lead, ...data });
      setEditData({ ...editData, ...data });
    } catch (e) {
      alert("Mark paid failed: " + (e?.message || "unknown"));
    }
  };

  const handleMarkRefund = async () => {
    if (!confirm("Mark this lead as refunded? This will move them to cold.")) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/demo/leads/${leadId}/mark-refund`, { method: "POST" });
      if (!r.ok) throw new Error("Refund failed");
      const data = await r.json();
      setLead({ ...lead, ...data });
      setEditData({ ...editData, ...data });
    } catch (e) {
      alert("Refund failed: " + (e?.message || "unknown"));
    }
  };

  const setPlanField = async (key, value) => {
    const updates = { [key]: value };
    if (key === "selectedPlan" && value && PLAN_DATA[value]) {
      // auto-fill amount when picking a known plan
      if (!editData.planAmount && !lead.planAmount) {
        updates.planAmount = PLAN_DATA[value].priceNum;
      }
    }
    setEditData({ ...editData, ...updates });
    setLead({ ...lead, ...updates });
    await updateLeadField(leadId, updates);
  };

  if (!lead) {
    return (
      <div className="ld-page ld-not-found">
        <Button variant="ghost" onClick={() => navigate("/demo/crm")}>
          <ArrowLeft className="w-4 h-4" /> Back to CRM
        </Button>
        <p>Lead not found</p>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.cold;
  const planKey = lead.selectedPlan || lead.recommendedPlan;
  const plan = planKey && PLAN_DATA[planKey] ? PLAN_DATA[planKey] : null;
  const action = getActionSuggestion(lead);
  const seq = lead.followUpSequence;

  const timeline = [
    lead.createdAt && { label: "Lead created", date: lead.createdAt, icon: "create" },
    lead.scheduledAt && { label: "Demo scheduled", date: lead.scheduledAt, icon: "schedule" },
    lead.demoDoneAt && { label: "Demo completed", date: lead.demoDoneAt, icon: "demo" },
    lead.paymentSentAt && { label: "Payment sent", date: lead.paymentSentAt, icon: "payment" },
    lead.convertedAt && { label: `Converted — $${lead.convertedAmount || 0}`, date: lead.convertedAt, icon: "convert" },
    lead.lastWhatsAppFollowUp && { label: "Last WA follow-up", date: lead.lastWhatsAppFollowUp, icon: "wa" },
    lead.fathomSyncedAt && { label: "Fathom synced", date: lead.fathomSyncedAt, icon: "fathom" },
  ].filter(Boolean).sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="ld-page" data-testid="lead-detail-page">
      {/* Top bar */}
      <div className="ld-topbar">
        <Button variant="ghost" size="sm" onClick={() => navigate("/demo/crm")} data-testid="ld-back-btn">
          <ArrowLeft className="w-4 h-4" /> Pipeline
        </Button>
        <div className="ld-topbar-center">
          <h1 className="ld-name">{lead.clientName} {lead.clientLastName || ""}</h1>
          <span className="ld-status-badge" style={{ color: statusCfg.color, background: statusCfg.bg }} data-testid="ld-status-badge">
            {statusCfg.label}
          </span>
        </div>
        <div className="ld-topbar-right">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditData(lead); }}>Cancel</Button>
              <Button size="sm" onClick={handleSave} className="ld-save-btn" data-testid="ld-save-btn">
                <Save className="w-3.5 h-3.5" /> Save
              </Button>
            </>
          ) : (
            <>
              {saved && <span className="ld-saved-badge"><Check className="w-3 h-3" /> Saved</span>}
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="ld-edit-btn">Edit</Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                data-testid="ld-delete-btn"
                title="Delete lead"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              {lead.status === "converted" || lead.paymentStatus === "paid" ? (
                <Button size="sm" variant="outline" onClick={() => setActiveTab("overview")} data-testid="ld-view-receipt-btn" title="Lead is paid — start demo disabled">
                  <DollarSign className="w-3.5 h-3.5" /> Paid · view receipt
                </Button>
              ) : (
                <Button size="sm" onClick={() => navigate(`/demo/live/${leadId}`)} className="ld-demo-btn" data-testid="ld-start-demo-btn">
                  <Play className="w-3.5 h-3.5" /> Start Demo
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="ld-tabs" data-testid="ld-tabs">
        <button className={`ld-tab ${activeTab === "overview" ? "ld-tab-active" : ""}`} onClick={() => setActiveTab("overview")} data-testid="ld-tab-overview">
          <User className="w-3.5 h-3.5" /> Overview
        </button>
        <button className={`ld-tab ${activeTab === "whatsapp" ? "ld-tab-active" : ""}`} onClick={() => setActiveTab("whatsapp")} data-testid="ld-tab-whatsapp">
          <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
          {msgCount > 0 && <span className="ld-tab-count">{msgCount}</span>}
        </button>
        <button className={`ld-tab ${activeTab === "intel" ? "ld-tab-active" : ""}`} onClick={() => setActiveTab("intel")} data-testid="ld-tab-intel">
          <FileText className="w-3.5 h-3.5" /> Call Intel
        </button>
        <button className={`ld-tab ${activeTab === "timeline" ? "ld-tab-active" : ""}`} onClick={() => setActiveTab("timeline")} data-testid="ld-tab-timeline">
          <Clock className="w-3.5 h-3.5" /> Timeline
        </button>
      </div>

      {/* Action suggestion */}
      {action.text && (
        <div className={`ld-action-bar ld-action-${action.color}`} data-testid="ld-action-bar">
          <AlertTriangle className="w-4 h-4" />
          <span>{action.text}</span>
        </div>
      )}

      <div className="ld-content">
        {/* ── Overview Tab ── */}
        {activeTab === "overview" && (
          <div className="ld-overview" data-testid="ld-overview">
            <div className="ld-grid-2col">
              {/* Left: Lead Info */}
              <div className="ld-info-card">
                <h3 className="ld-card-title">Lead Information</h3>
                <div className="ld-info-rows">
                  <InfoRow icon={<User className="w-3.5 h-3.5" />} label="Name" editing={editing}
                    value={`${lead.clientName} ${lead.clientLastName || ""}`}
                    editNode={
                      <div className="ld-edit-row">
                        <Input value={editData.clientName || ""} onChange={e => setEditData({...editData, clientName: e.target.value})} placeholder="First" data-testid="ld-edit-name" />
                        <Input value={editData.clientLastName || ""} onChange={e => setEditData({...editData, clientLastName: e.target.value})} placeholder="Last" />
                      </div>
                    }
                  />
                  <InfoRow icon={<Phone className="w-3.5 h-3.5" />} label="WhatsApp" value={lead.whatsappNumber || "—"} editing={editing}
                    editNode={<Input value={editData.whatsappNumber || ""} onChange={e => setEditData({...editData, whatsappNumber: e.target.value})} data-testid="ld-edit-phone" />}
                  />
                  <InfoRow icon={<Briefcase className="w-3.5 h-3.5" />} label="Target Role" value={lead.targetRole || "—"} editing={editing}
                    editNode={<Input value={editData.targetRole || ""} onChange={e => setEditData({...editData, targetRole: e.target.value})} />}
                  />
                  <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Market" value={lead.targetMarket || "—"} editing={editing}
                    editNode={<Input value={editData.targetMarket || ""} onChange={e => setEditData({...editData, targetMarket: e.target.value})} />}
                  />
                  <InfoRow icon={<User className="w-3.5 h-3.5" />} label="Closer" value={lead.closerName || "Unassigned"} editing={editing}
                    editNode={
                      <select
                        className="ld-closer-select"
                        value={editData.closerUsername || ""}
                        onChange={e => {
                          const u = e.target.value;
                          const match = (CLOSER_ACCOUNTS || []).find(c => c.username === u);
                          setEditData({ ...editData, closerUsername: u, closerName: match?.name || editData.closerName });
                        }}
                        data-testid="ld-edit-closer"
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13 }}
                      >
                        <option value="">— Unassigned —</option>
                        {(CLOSER_ACCOUNTS || []).map(c => (
                          <option key={c.username} value={c.username}>{c.name} ({c.username})</option>
                        ))}
                      </select>
                    }
                  />
                  <InfoRow icon={<Calendar className="w-3.5 h-3.5" />} label="Scheduled" value={formatDate(lead.scheduledAt)} />
                  {lead.leadSource && lead.leadSource.sourceType && (
                    <InfoRow icon={<ExternalLink className="w-3.5 h-3.5" />} label="Lead Source"
                      value={
                        <span className="ld-source-badge" data-testid="ld-lead-source">
                          <span className={`ld-source-type ld-source-${lead.leadSource.sourceType}`}>
                            {lead.leadSource.sourceType === "ad" ? "CTWA Ad" : lead.leadSource.sourceType === "organic" ? "Organic" : lead.leadSource.sourceType}
                          </span>
                          {lead.leadSource.headline && <span className="ld-source-headline">{lead.leadSource.headline}</span>}
                          {lead.leadSource.sourceUrl && (
                            <a href={lead.leadSource.sourceUrl} target="_blank" rel="noopener noreferrer" className="ld-source-link">View ad</a>
                          )}
                        </span>
                      }
                    />
                  )}
                </div>
                {lead.notes && (
                  <div className="ld-notes">
                    <Label>Notes</Label>
                    {editing ? (
                      <textarea value={editData.notes || ""} onChange={e => setEditData({...editData, notes: e.target.value})} className="ld-notes-textarea" data-testid="ld-edit-notes" />
                    ) : (
                      <p>{lead.notes}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Status + Plan + Sequence */}
              <div className="ld-right-col">
                {/* Status actions */}
                <div className="ld-info-card">
                  <h3 className="ld-card-title">Status</h3>
                  <div className="ld-status-actions" data-testid="ld-status-actions">
                    {lead.status === "upcoming" && (
                      <>
                        <Button size="sm" onClick={() => updateStatus("demo_done", { demoDoneAt: new Date().toISOString() })} className="ld-btn-teal" data-testid="mark-demo-done">Mark demo done</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("demo_in_progress")} data-testid="mark-demo-progress">Demo in progress</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("cold")}>Cancel demo</Button>
                      </>
                    )}
                    {lead.status === "demo_in_progress" && (
                      <>
                        <Button size="sm" onClick={() => updateStatus("demo_done", { demoDoneAt: new Date().toISOString() })} className="ld-btn-teal" data-testid="mark-demo-done">Mark demo done</Button>
                      </>
                    )}
                    {lead.status === "demo_done" && (
                      <>
                        <Button size="sm" onClick={() => updateStatus("payment_sent", { paymentSentAt: new Date().toISOString() })} className="ld-btn-teal">Mark payment sent</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("cold")}>Mark cold</Button>
                      </>
                    )}
                    {lead.status === "payment_sent" && (
                      <>
                        <Button size="sm" onClick={() => {
                          const p = lead.recommendedPlan ? PLAN_DATA[lead.recommendedPlan] : null;
                          updateStatus("converted", { convertedAt: new Date().toISOString(), convertedAmount: p?.priceNum || 0 });
                        }} className="ld-btn-teal">Mark converted</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("cold")}>Mark cold</Button>
                      </>
                    )}
                    {lead.status === "cold" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("new")}>Move to New</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("demo_done")}>Move to Demo Done</Button>
                      </>
                    )}
                    {lead.status === "converted" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("payment_sent")}>Move back to Payment Sent</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus("demo_done")}>Move back to Demo Done</Button>
                      </>
                    )}
                  </div>
                  {/* Send PDF button for post-demo leads */}
                  {(lead.status === "demo_done" || lead.status === "payment_sent" || lead.status === "converted") && lead.whatsappNumber && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        setPdfSending(true);
                        try {
                          // Generate a simple PDF summary
                          const { jsPDF } = await import("jspdf");
                          const doc = new jsPDF();
                          doc.setFontSize(20);
                          doc.text("BulkJobApply - Strategy Report", 20, 25);
                          doc.setFontSize(12);
                          doc.text(`Prepared for: ${lead.clientName} ${lead.clientLastName || ""}`, 20, 40);
                          if (lead.targetRole) doc.text(`Target Role: ${lead.targetRole}`, 20, 50);
                          if (lead.targetMarket) doc.text(`Market: ${lead.targetMarket}`, 20, 60);
                          if (lead.recommendedPlan) {
                            const p = PLAN_DATA[lead.recommendedPlan];
                            if (p) {
                              doc.text(`Recommended Plan: ${p.name} - ${p.price}`, 20, 75);
                              p.features.forEach((f, i) => doc.text(`  - ${f}`, 25, 87 + i * 8));
                            }
                          }
                          doc.text("Next Steps:", 20, 140);
                          doc.text("1. Complete payment via PayPal", 25, 150);
                          doc.text("2. WhatsApp group setup within 1 hour", 25, 158);
                          doc.text("3. Applications start Day 3", 25, 166);
                          doc.text("Payment: https://paypal.me/knowfinity", 20, 185);
                          doc.text("Questions? Reply on WhatsApp anytime!", 20, 200);
                          const base64 = doc.output("datauristring").split(",")[1];
                          await waSendPdf(lead.id, base64, `BulkJobApply_${lead.clientName}_Report.pdf`);
                          alert("PDF sent via WhatsApp!");
                          refreshLead();
                        } catch (e) {
                          alert("Failed: " + e.message);
                        } finally {
                          setPdfSending(false);
                        }
                      }}
                      disabled={pdfSending}
                      className="ld-send-pdf-btn"
                      data-testid="ld-send-pdf-btn"
                    >
                      {pdfSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {lead.pdfSentViaWa ? "Resend PDF via WA" : "Send PDF via WhatsApp"}
                    </Button>
                  )}
                </div>

                {/* Plan & Payment */}
                <div className="ld-info-card" data-testid="ld-plan-payment-card">
                  <h3 className="ld-card-title">Plan & Payment</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <Label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" }}>Selected plan</Label>
                      <select
                        className="ld-plan-select"
                        value={editData.selectedPlan || lead.selectedPlan || lead.recommendedPlan || ""}
                        onChange={e => setPlanField("selectedPlan", e.target.value)}
                        data-testid="ld-plan-select"
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, background: "#fff" }}
                      >
                        <option value="">— Pick a plan —</option>
                        {Object.entries(PLAN_DATA).map(([k, p]) => (
                          <option key={k} value={k}>{p.name} — {p.price}</option>
                        ))}
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <Label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" }}>Amount (USD)</Label>
                      <Input
                        type="number"
                        value={editData.planAmount ?? lead.planAmount ?? lead.convertedAmount ?? ""}
                        onChange={e => {
                          const v = e.target.value;
                          setEditData({ ...editData, planAmount: v });
                          setLead({ ...lead, planAmount: v });
                        }}
                        onBlur={() => {
                          const v = editData.planAmount;
                          if (v !== undefined) updateLeadField(leadId, { planAmount: Number(v) || 0 });
                        }}
                        placeholder="e.g. 625"
                        data-testid="ld-plan-amount"
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>Payment</span>
                      <span
                        data-testid="ld-payment-status"
                        style={{
                          fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
                          background: lead.paymentStatus === "paid" ? "#dcfce7" : lead.paymentStatus === "refunded" ? "#fee2e2" : "#fef3c7",
                          color: lead.paymentStatus === "paid" ? "#166534" : lead.paymentStatus === "refunded" ? "#991b1b" : "#92400e",
                        }}
                      >
                        {lead.paymentStatus === "paid" ? "PAID" : lead.paymentStatus === "refunded" ? "REFUNDED" : "PENDING"}
                      </span>
                      {lead.paidAt && <span style={{ fontSize: 11, color: "#6b7280" }}>· {formatDate(lead.paidAt)}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {lead.paymentStatus !== "paid" && (
                        <Button size="sm" className="ld-btn-teal" onClick={handleMarkPaid} data-testid="ld-mark-paid-btn">
                          <DollarSign className="w-3.5 h-3.5" /> Mark as paid
                        </Button>
                      )}
                      {lead.paymentStatus === "paid" && (
                        <Button size="sm" variant="outline" onClick={handleMarkRefund} data-testid="ld-mark-refund-btn" className="text-red-600">
                          Mark refund
                        </Button>
                      )}
                    </div>
                    {plan && (
                      <div className="ld-plan-box" style={{ marginTop: 8 }}>
                        <span className="ld-plan-name">{plan.name}</span>
                        <span className="ld-plan-price">{plan.price}</span>
                        <ul className="ld-plan-features">
                          {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sequence */}
                <div className="ld-info-card">
                  <h3 className="ld-card-title">Follow-up Sequence</h3>
                  {seq ? (
                    <div className="ld-sequence" data-testid="ld-sequence">
                      <div className="ld-seq-status">
                        <span className={`ld-seq-badge ${seq.active && !seq.paused ? "ld-seq-active" : seq.paused ? "ld-seq-paused" : "ld-seq-inactive"}`}>
                          {seq.active && !seq.paused ? "Active" : seq.paused ? "Paused" : "Inactive"}
                        </span>
                        <span className="ld-seq-step">Step {seq.currentStep || 0} of {followUpRules.length || 5}</span>
                      </div>
                      {seq.paused && seq.pauseReason && (
                        <div className="ld-seq-pause-reason">
                          Paused — {seq.pauseReason === "lead_replied" ? "lead replied" : seq.pauseReason === "closer_takeover" ? "closer takeover" : seq.pauseReason === "manual" ? "manually paused" : seq.pauseReason}
                        </div>
                      )}
                      <div className="ld-seq-actions">
                        {seq.active && !seq.paused && (
                          <Button size="sm" variant="outline" onClick={() => handleSequenceAction("pause")} disabled={seqLoading} data-testid="ld-seq-pause">
                            <Pause className="w-3.5 h-3.5" /> Pause
                          </Button>
                        )}
                        {seq.paused && (
                          <Button size="sm" onClick={() => handleSequenceAction("resume")} disabled={seqLoading} className="ld-btn-teal" data-testid="ld-seq-resume">
                            <Play className="w-3.5 h-3.5" /> Resume
                          </Button>
                        )}
                      </div>
                      {/* Step-by-step breakdown */}
                      <div className="ld-seq-steps" data-testid="ld-seq-steps">
                        {(followUpRules.length > 0 ? followUpRules : [
                          {step: 1, label: "Immediate follow-up", dayAfterDemo: 0},
                          {step: 2, label: "Day 1 check-in", dayAfterDemo: 1},
                          {step: 3, label: "Day 3 case study", dayAfterDemo: 3},
                          {step: 4, label: "Day 5 scarcity push", dayAfterDemo: 5},
                          {step: 5, label: "Day 7 final close", dayAfterDemo: 7},
                        ]).map(rule => {
                          const completed = seq.completedSteps?.find(c => c.step === rule.step);
                          const isSending = stepSending === rule.step;
                          return (
                            <div key={rule.step} className={`ld-seq-step-row ${completed ? "ld-step-done" : ""}`} data-testid={`ld-step-${rule.step}`}>
                              <div className="ld-step-indicator">
                                {completed ? <Check className="w-3.5 h-3.5" /> : <span className="ld-step-num">{rule.step}</span>}
                              </div>
                              <div className="ld-step-info">
                                <span className="ld-step-label">{rule.label}</span>
                                <span className="ld-step-day">Day {rule.dayAfterDemo}</span>
                                {completed && <span className="ld-step-sent">{formatDate(completed.sentAt)}</span>}
                              </div>
                              {!completed && (seq.active || seq.paused) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="ld-step-send-btn"
                                  onClick={() => handleExecuteStep(rule.step)}
                                  disabled={isSending || stepSending !== null}
                                  data-testid={`ld-step-send-${rule.step}`}
                                >
                                  {isSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                  {isSending ? "Sending..." : "Send now"}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="ld-seq-empty">
                      <p>No sequence started</p>
                      {(lead.status === "demo_done" || lead.status === "payment_sent") && (
                        <Button size="sm" onClick={() => handleSequenceAction("start")} disabled={seqLoading} className="ld-btn-teal" data-testid="ld-seq-start">
                          {seqLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          Start sequence
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Quick stats */}
                <div className="ld-info-card">
                  <h3 className="ld-card-title">Quick Stats</h3>
                  <div className="ld-stats-grid">
                    <div className="ld-stat"><span className="ld-stat-val">{lead.weeklyApps || "—"}</span><span className="ld-stat-lbl">Weekly apps</span></div>
                    <div className="ld-stat"><span className="ld-stat-val">{lead.targetSalary ? `$${lead.targetSalary}` : "—"}</span><span className="ld-stat-lbl">Target salary</span></div>
                    <div className="ld-stat"><span className="ld-stat-val">{lead.monthsSearching ?? "—"}</span><span className="ld-stat-lbl">Months searching</span></div>
                    <div className="ld-stat"><span className="ld-stat-val">{lead.callDurationMinutes ? `${lead.callDurationMinutes}m` : "—"}</span><span className="ld-stat-lbl">Call duration</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── WhatsApp Tab ── */}
        {activeTab === "whatsapp" && (
          <div className="ld-whatsapp" data-testid="ld-whatsapp">
            <WhatsAppChat lead={lead} onClose={() => setActiveTab("overview")} />
          </div>
        )}

        {/* ── Call Intel Tab ── */}
        {activeTab === "intel" && (
          <div className="ld-intel" data-testid="ld-intel">
            <CallIntelligence lead={lead} onUpdate={refreshLead} />
          </div>
        )}

        {/* ── Timeline Tab ── */}
        {activeTab === "timeline" && (
          <div className="ld-timeline" data-testid="ld-timeline">
            <div className="ld-timeline-list">
              {timeline.length === 0 ? (
                <p className="ld-timeline-empty">No events yet</p>
              ) : (
                timeline.map((evt, i) => (
                  <div key={i} className="ld-tl-item">
                    <div className="ld-tl-dot" />
                    <div className="ld-tl-content">
                      <span className="ld-tl-label">{evt.label}</span>
                      <span className="ld-tl-date">{formatDate(evt.date)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}>
        <AlertDialogContent data-testid="ld-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{lead.clientName} {lead.clientLastName || ""}</strong>
              {lead.whatsappNumber && <span> ({lead.whatsappNumber})</span>}
              <br /><br />
              This permanently removes the lead, their WhatsApp chat history, and all reminders.
              <br /><strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="ld-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteLead(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="ld-delete-confirm"
            >
              {deleting ? "Deleting…" : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoRow({ icon, label, value, editing, editNode }) {
  return (
    <div className="ld-info-row">
      <div className="ld-info-row-icon">{icon}</div>
      <div className="ld-info-row-content">
        <span className="ld-info-label">{label}</span>
        {editing && editNode ? editNode : <span className="ld-info-value">{value}</span>}
      </div>
    </div>
  );
}
